import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, TextField, IconButton, InputAdornment, Slider } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Colors from '../../library/Colors';

// Shared building blocks for the webshop-admin screens, so they stop
// re-declaring (and drifting on) the same tokens/components. Keeps the existing
// hex-token approach (deliberately not the big theme-token migration) but in
// ONE place, so every screen resolves the same values.

const hexToRgba = (hex, a) => {
    const h = String(hex).replace('#', '');
    const n = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
    const r = parseInt(n.slice(0, 2), 16), g = parseInt(n.slice(2, 4), 16), b = parseInt(n.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${a})`;
};

// The token block that used to live (verbatim) in every screen.
export const webshopTokens = (theme) => {
    const isDark = theme === 'dark';
    return {
        isDark,
        cardBg: isDark ? '#2d333b' : '#ffffff',
        borderColor: isDark ? '#3a3f47' : '#e4e7eb',
        textColor: isDark ? Colors.dark_text : Colors.light_text,
        mutedText: isDark ? '#b0b8c1' : '#768390',
        panelBg: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
    };
};

// One modal-sizing system for the webshop-admin content/editor dialogs, so the
// Opmaak section modals and the Pages modals (block config, product container,
// designers) all read at the same generous width instead of drifting per-modal.
// Spread the result straight onto a MUI <Dialog>: {...webshopModalSizing('wide')}.
//
//   wide    – multi-column card modals + full editors (Menu bar, Footer, Product
//             detail, product container, block designers). Fills wide screens.
//   medium  – single-card / focused config (Image gallery, Global widths,
//             Language bar). Roomy, but not so wide it reads as empty.
//   compact – short forms and confirmations.
//
// Pass { collapsed: true } (sidebar hidden) to let wide modals grow a touch more.
const MODAL_TIERS = {
    wide:   { width: '94vw', widthCollapsed: '96vw', max: 1700, maxCollapsed: 1850 },
    medium: { width: '92vw', widthCollapsed: '92vw', max: 1280, maxCollapsed: 1280 },
};
/**
 * Upper bound of every "maximum width" control (sliders and number fields).
 * 2560 = a QHD/ultrawide screen; the storefront, the mini-api and the legacy
 * admin impose no limit of their own. One constant, so the controls can no
 * longer drift apart (they used to stop at 2000, 2400 and 2500).
 */
export const WEBSHOP_MAX_WIDTH_PX = 2560;

/**
 * Width of a small number field with a "px" suffix. It has to show four
 * digits (up to WEBSHOP_MAX_WIDTH_PX) next to the suffix AND the browser's
 * spin buttons; at 110-120px the last digit was cut off.
 */
export const WEBSHOP_PX_FIELD_WIDTH = 150;

const decimalsOf = (step) => {
    const s = String(step);
    return s.includes('.') ? s.split('.')[1].length : 0;
};

/**
 * A slider that can always be typed into as well: every slider in the webshop
 * admin goes through this, so dragging and typing behave the same everywhere.
 *
 * - Dragging updates the value live (as before).
 * - Typing updates live too, as soon as the text is a valid number in range.
 *   A value that is empty or out of range is left alone while typing, so
 *   "1" on the way to "1200" is not clamped to the minimum; on blur or Enter
 *   it is clamped to [min, max] and rounded to the step's precision.
 * - A saved value outside the range is shown as-is (the slider stops at its
 *   end); it only changes when the user edits it.
 *
 * `unit` is the suffix in the field ("px", "%"). The field is wide enough for
 * four digits + suffix whenever there is a unit, narrower for bare counts.
 */
export const SliderField = ({
    value, onChange, min, max, step = 1, unit = '', label, fieldWidth, disabled = false,
    marks = false, valueLabelFormat, sx = {},
}) => {
    const decimals = decimalsOf(step);
    const numeric = typeof value === 'number' ? value : Number(value);
    const current = Number.isFinite(numeric) ? numeric : min;
    const [draft, setDraft] = React.useState(String(current));
    const [focused, setFocused] = React.useState(false);
    // follow outside changes (slider, preset buttons, loading) unless the
    // user is typing in this very field
    React.useEffect(() => { if (!focused) setDraft(String(current)); }, [current, focused]);

    const parse = (text) => {
        const n = parseFloat(String(text).replace(',', '.'));
        return Number.isFinite(n) ? n : null;
    };
    const round = (n) => (decimals ? Number(n.toFixed(decimals)) : Math.round(n));
    const commit = () => {
        const n = parse(draft);
        const next = n === null ? current : round(Math.min(max, Math.max(min, n)));
        setDraft(String(next));
        if (next !== current) onChange(next);
    };

    // one width for every field with a unit, so fields in the same card end on
    // the same line (a 3-digit "px" next to a 4-digit one used to jag)
    const width = fieldWidth || (unit || String(max).length >= 4 ? WEBSHOP_PX_FIELD_WIDTH : 96);
    const fmt = valueLabelFormat || ((v) => (unit ? `${v}${unit}` : String(v)));
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, ...sx }}>
            <Slider
                size="small"
                min={min}
                max={max}
                step={step}
                value={Math.min(max, Math.max(min, current))}
                onChange={(_e, v) => onChange(v)}
                valueLabelDisplay="auto"
                valueLabelFormat={fmt}
                marks={marks}
                disabled={disabled}
                aria-label={label}
                sx={{ flex: 1, minWidth: 80 }}
            />
            <TextField
                type="number"
                size="small"
                value={draft}
                disabled={disabled}
                onFocus={() => setFocused(true)}
                onBlur={() => { setFocused(false); commit(); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
                onChange={(e) => {
                    const text = e.target.value;
                    setDraft(text);
                    const n = parse(text);
                    if (n !== null && n >= min && n <= max) {
                        const next = round(n);
                        if (next !== current) onChange(next);
                    }
                }}
                inputProps={{ min, max, step, inputMode: decimals ? 'decimal' : 'numeric', 'aria-label': label }}
                InputProps={unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : undefined}
                sx={{ width, flexShrink: 0 }}
            />
        </Box>
    );
};
SliderField.propTypes = {
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onChange: PropTypes.func.isRequired,
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
    step: PropTypes.number,
    unit: PropTypes.string,
    label: PropTypes.string,
    fieldWidth: PropTypes.number,
    disabled: PropTypes.bool,
    marks: PropTypes.bool,
    valueLabelFormat: PropTypes.func,
    sx: PropTypes.object,
};

export const webshopModalSizing = (tier = 'compact', { collapsed = false, sx = {} } = {}) => {
    const t = MODAL_TIERS[tier];
    if (!t) {
        // compact / unknown → MUI's own small breakpoint.
        return { maxWidth: 'sm', fullWidth: true, PaperProps: { sx: { borderRadius: 3, maxHeight: '92vh', ...sx } } };
    }
    return {
        maxWidth: false,
        fullWidth: true,
        PaperProps: { sx: {
            borderRadius: 3,
            maxHeight: '92vh',
            width: collapsed ? t.widthCollapsed : t.width,
            maxWidth: collapsed ? t.maxCollapsed : t.max,
            ...sx,
        } },
    };
};

// Tinted icon "medallion". The background tint is derived from the SAME colour
// as the glyph (brand blue by default), so the two blues no longer clash.
export const iconBoxSx = (isDark, color = Colors.sft_blue) => ({
    width: 36,
    height: 36,
    borderRadius: 1.25,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: hexToRgba(color, isDark ? 0.16 : 0.09),
    color,
    flexShrink: 0,
    '& svg': { fontSize: 20 },
});

// One page-title component: "h4 / 500" heading + muted body2 description, above
// the content card. Optional right-aligned `action` slot.
export const WebshopPageHeader = ({ title, description, theme, action, t }) => {
    const { textColor, mutedText } = webshopTokens(theme);
    return (
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="h4" sx={{ color: textColor, fontWeight: 500, mb: 0.5 }}>{title}</Typography>
                {description && <Typography variant="body2" sx={{ color: mutedText }}>{description}</Typography>}
            </Box>
            <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {action}
            </Box>
        </Box>
    );
};
WebshopPageHeader.propTypes = {
    title: PropTypes.node,
    description: PropTypes.node,
    theme: PropTypes.string,
    action: PropTypes.node,
    t: PropTypes.func,
};

// One password field with a show/hide toggle.
export const WebshopPasswordField = ({ label, value, onChange, visible, onToggle, placeholder, size = 'small', fullWidth = true, ...rest }) => (
    <TextField
        label={label}
        size={size}
        fullWidth={fullWidth}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        InputProps={{
            endAdornment: (
                <InputAdornment position="end">
                    <IconButton size="small" onClick={onToggle} edge="end" tabIndex={-1}>
                        {visible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                </InputAdornment>
            ),
        }}
        {...rest}
    />
);
WebshopPasswordField.propTypes = {
    label: PropTypes.node,
    value: PropTypes.any,
    onChange: PropTypes.func,
    visible: PropTypes.bool,
    onToggle: PropTypes.func,
    placeholder: PropTypes.string,
    size: PropTypes.string,
    fullWidth: PropTypes.bool,
};
